<template>
  <wui-modal
    :size="40"
    :closable="false"
    class="flex h-160! max-h-[calc(100vh-32px)]! w-[min(32.5rem,calc(100vw-32px))]! flex-col overflow-hidden! p-0! [--modal-gap:0px] [--modal-pad:0px] [--modal-title-pad-y:0px] [&_.wui-modal__content]:min-h-0 [&_.wui-modal__content]:min-w-0 [&_.wui-modal__content]:flex-1 [&_.wui-modal__content]:overflow-hidden! [&_.wui-modal__footer]:w-full [&_.wui-modal__footer]:flex-col [&_.wui-modal__footer]:items-stretch [&_.wui-modal__footer]:justify-start [&_.wui-modal__header]:w-full"
    data-test="add-chat-participants-modal"
  >
    <template #header>
      <div class="flex w-full items-center justify-between gap-2 px-4 pt-4">
        <h3 class="text-heading-sb-18-24 text-fg-base-fg3">
          {{ $t('calls.addParticipants') }}
        </h3>
        <wui-btn
          icon
          variant="neut"
          state="alpha"
          :size="32"
          prepend-icon="closeRegularMdM"
          data-test="add-chat-participants-close"
          @click="onClose"
        />
      </div>
    </template>

    <div class="flex h-full min-h-0 min-w-0 flex-col overflow-x-hidden pt-1.5">
      <div class="px-4 flex h-full min-h-0 min-w-0 flex-col overflow-x-hidden">
        <div
          class="flex min-w-0 shrink-0 items-stretch gap-1 rounded-12 border border-surf-brand-base-def bg-bg-base-bg1"
          :class="selectedContacts.length ? 'px-2 py-1.5' : 'px-3 py-1.5'"
          data-test="add-chat-participants-search"
        >
          <div
            class="flex min-w-0 grow flex-wrap items-center gap-1 overflow-y-auto"
            :class="selectedContacts.length ? 'max-h-36 min-h-0' : 'min-h-0'"
          >
            <wui-chip
              v-for="contact in selectedContacts"
              :key="contact.id"
              :size="32"
              variant="neut"
              state="tonal"
              removable
              class="max-w-full min-w-0"
              :label="contact.name || $t('common.noName')"
              :title="contact.name || $t('common.noName')"
              @remove="removeSelected(contact.id)"
            >
              <template #prepend="{ square }">
                <contact-avatar
                  :contact="contact"
                  :contact-type="contact.type"
                  :size="24"
                  :square="square"
                  class="shrink-0"
                />
              </template>
            </wui-chip>
            <input
              v-model="search"
              type="text"
              class="min-h-6 min-w-0 grow bg-transparent py-0.5 text-body-r-14-22 text-fg-base-fg3 outline-none placeholder:text-fg-base-fg32"
              :placeholder="selectedContacts.length ? '' : $t('chat.addParticipantsSearchPlaceholder')"
              data-test="add-chat-participants-search-input"
            >
          </div>
          <div
            class="flex w-6 shrink-0 flex-col items-center"
            :class="selectedContacts.length ? 'min-h-[52px] justify-between self-stretch' : 'justify-center'"
          >
            <wui-btn
              v-if="hasSearchQuery"
              icon
              variant="neut"
              state="text"
              :size="24"
              prepend-icon="clear"
              data-test="add-chat-participants-clear-search"
              :title="$t('common.clear')"
              @click="clearSearch"
            />
            <wui-icon
              v-else
              name="searchM"
              class="text-fg-base-fg32"
              small
            />
            <wui-btn
              v-if="selectedContacts.length"
              icon
              variant="neut"
              state="text"
              :size="24"
              prepend-icon="trashM"
              class="text-[var(--color-fld-def-btn-rght-def)]!"
              data-test="add-chat-participants-clear-all"
              :title="$t('common.clear')"
              @click="clearSelected"
            />
          </div>
        </div>

        <div
          class="min-h-0 min-w-0 flex-1 overflow-x-hidden mt-3"
          data-test="add-chat-participants-list"
        >
          <virtual-scroll
            v-if="visibleContacts.length"
            class="h-full min-w-0 overflow-x-hidden overflow-y-auto"
            field="id"
            :items="visibleContacts"
            :item-height="52"
            :padding="0"
            :loading="false"
          >
            <template #item="{ item: contact }">
              <button
                type="button"
                class="flex h-[52px] w-full min-w-0 items-center gap-3 rounded-8 px-1 text-left hover:bg-bg-base-bg3"
                :data-test="`add-chat-participants-item-${contact.id}`"
                @click="toggleSelected(contact)"
              >
                <contact-avatar
                  :contact="contact"
                  :contact-type="contact.type"
                  :size="40"
                  class="shrink-0"
                />
                <span class="min-w-0 grow truncate text-body-m-15-22 text-fg-base-fg3">
                  {{ contact.name || $t('common.noName') }}
                </span>
                <wui-checkbox
                  :model-value="selectedIds.has(contact.id)"
                  class="pointer-events-none shrink-0"
                  small
                  readonly
                />
              </button>
            </template>
          </virtual-scroll>
          <p
            v-else
            class="px-3 py-8 text-center text-body-r-15-22 text-fg-base-fg17"
          >
            {{ $t('common.nothingFound') }}
          </p>
        </div>
      </div>
      <div class="w-full border-t border-bor-nav" />
    </div>

    <template #footer>
      <div class="flex w-full flex-col pb-4">
        <div class="flex w-full min-w-0 flex-wrap items-center justify-end gap-2 px-4 pt-2">
          <wui-btn
            variant="neut"
            state="tonal"
            :size="48"
            class="min-w-0"
            data-test="add-chat-participants-cancel"
            @click="onClose"
          >
            {{ $t('common.cancel') }}
          </wui-btn>
          <wui-btn
            variant="brand"
            state="filled"
            :size="48"
            class="min-w-0"
            :disabled="!selectedContacts.length || isSubmitting"
            auto-loading
            data-test="add-chat-participants-submit"
            @click="onSubmit"
          >
            <span class="flex items-center gap-2">
              {{ $t('common.add') }}
              <span
                v-if="selectedContacts.length"
                class="flex min-w-5 items-center justify-center rounded-full bg-bg-base-bg1 px-1.5 text-label-m-12-16 text-surf-brand-base-def"
              >
                {{ selectedContacts.length }}
              </span>
            </span>
          </wui-btn>
        </div>
      </div>
    </template>
  </wui-modal>
</template>

<script setup lang="ts">
import {
  closeDialogKey,
  safeInject,
  useRenderNotifier,
  WuiBtn,
  WuiCheckbox,
  WuiChip,
  WuiIcon,
  WuiModal,
  WuiVirtualScroll,
} from '@wui/common-library'
import { isBotPServed } from '@wui/im'
import { storeToRefs } from 'pinia'
import { Component, computed, ref } from 'vue'

import { useStoreContacts } from '@/entities/contact'
import { ContactAvatar } from '@/entities/contact-avatar'
import { useRoom } from '@/entities/use-room'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import { Contact } from '@/shared/types/api'
import { Room } from '@/shared/types/store'

const VirtualScroll = WuiVirtualScroll as Component

const props = defineProps<{
  room: Room
}>()

const closeDialog = safeInject(closeDialogKey)
const { t } = useLocalization()
const { notifySuccess, notifyWarning } = useRenderNotifier()
const { addMember } = useRoom()
const { currentUser } = useCurrentUser()
const contactsStore = useStoreContacts()
const { allContacts } = storeToRefs(contactsStore)
const { findByPServed } = contactsStore

const search = ref('')
const selectedIds = ref(new Set<string>())
const isSubmitting = ref(false)

const hasSearchQuery = computed(() => Boolean(search.value.trim()))

const clearSearch = () => {
  search.value = ''
}

const alreadyMemberIds = computed(() => {
  const ids = new Set<string>()
  props.room.contacts?.forEach((member) => {
    ids.add(member.id)
    const fromStore = findByPServed(member.id)
    if (fromStore?.id) {
      ids.add(fromStore.id)
    }
    if (fromStore?.imLogin) {
      ids.add(fromStore.imLogin)
    }
  })
  props.room.subscribers?.forEach((contact) => {
    ids.add(contact.id)
    if (contact.imLogin) {
      ids.add(contact.imLogin)
    }
  })
  return ids
})

const isAlreadyMember = (contact: Contact) => {
  return alreadyMemberIds.value.has(contact.id)
    || Boolean(contact.imLogin && alreadyMemberIds.value.has(contact.imLogin))
    || Boolean(contact.mobilePhone && alreadyMemberIds.value.has(contact.mobilePhone))
}

const availableContacts = computed(() => {
  return allContacts.value.filter((contact) => {
    if (isAlreadyMember(contact)) {
      return false
    }
    if (contact.imLogin && isBotPServed(contact.imLogin)) {
      return false
    }
    if (contact.imLogin && contact.imLogin === currentUser.value?.imLogin) {
      return false
    }
    return true
  })
})

const visibleContacts = computed(() => {
  const query = search.value.trim().toLowerCase()
  if (!query) {
    return availableContacts.value
  }
  return availableContacts.value.filter((contact) => {
    const haystack = [
      contact.name,
      contact.internalNumber,
      contact.mobilePhone,
      contact.email,
      contact.imLogin,
    ].join(' ').toLowerCase()
    return haystack.includes(query)
  })
})

const selectedContacts = computed(() => {
  const byId = new Map(availableContacts.value.map((contact) => [contact.id, contact]))
  return [...selectedIds.value]
    .map((id) => byId.get(id))
    .filter((contact): contact is Contact => Boolean(contact))
})

const toggleSelected = (contact: Contact) => {
  const next = new Set(selectedIds.value)
  if (next.has(contact.id)) {
    next.delete(contact.id)
  } else {
    next.add(contact.id)
  }
  selectedIds.value = next
}

const removeSelected = (id: string) => {
  const next = new Set(selectedIds.value)
  next.delete(id)
  selectedIds.value = next
}

const clearSelected = () => {
  selectedIds.value = new Set()
}

const getAddMemberTarget = (contact: Contact) => {
  if (contact.imLogin && isBotPServed(contact.imLogin)) {
    return contact.imLogin
  }
  return contact.internalNumber || contact.id
}

const onClose = () => {
  closeDialog(undefined)
}

const onSubmit = async () => {
  if (!selectedContacts.value.length || isSubmitting.value) {
    return
  }
  isSubmitting.value = true
  let successCount = 0
  try {
    for (const contact of selectedContacts.value) {
      try {
        const target = getAddMemberTarget(contact)
        if (!target) {
          continue
        }
        const added = await addMember(props.room.pServedUser, target, 'AddMember')
        if (added) {
          successCount += 1
        }
      } catch {
        // continue adding remaining contacts
      }
    }
    if (successCount > 0) {
      notifySuccess(t('calls.participantsAdded'))
      onClose()
    } else {
      notifyWarning(t('calls.commandAcceptedError'))
    }
  } finally {
    isSubmitting.value = false
  }
}
</script>
